import React, { useEffect, useState, useCallback } from "react";

// Components
import Table from "./Childs/Table";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import Modal from "../../Common/Modal";
import Popup from "../../Common/Popup";

// Context
import { useWallet } from "../../../context/WalletContextProvider";
import { useGlobal } from "../../../context/GlobalContextProvider";

// Config
import error from "../../../config/error.json";
import { useError } from "../../../context/ErrorContextProvider";

// Constants
const DESCRIPTION_TEXT = 
  "When a Sponsor creates a Campaign and they would like to use your Art Pack, you can give them permission here.";

const SUCCESS_MESSAGE = "Great! the campaign is live and ready to go!";

// API Service
const approvedCampaignAPI = {
  getAll: async (account) => {
    const res = await fetch(`/api/artist/${account}/approvedcampaign`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },

  getArtPacks: async (account) => {
    const res = await fetch(`/api/artist/${account}/artpack`);
    if (res.status === 200) {
      return res.json();
    }
    return [];
  },

  getCampaignDetails: async (campaignId) => {
    try {
      const res = await fetch(`/api/contract/campaign/${campaignId}`);
      if (res.status === 204 || !res.ok) return null;
      return res.json();
    } catch (error) {
      console.error(`Error fetching campaign ${campaignId}:`, error);
      return null;
    }
  },
};

// Error handling helper
const parseContractError = (err) => {
  if (!err) return error.UNKNOWN_ERROR;

  // Handle error structure
  if (err.error) {
    err.data = err.error.data ? err.error.data : err.error;
  }

  // Parse error message
  if (err.data && err.data.message) {
    const customMessage = err.data.message.split(":");
    if (customMessage.length > 1 && error[customMessage[1].trim()]) {
      return error[customMessage[1].trim()];
    }
    
    if ([32000].includes(Math.abs(err.data.code))) {
      return error[Math.abs(err.code)];
    }
    
    return err.data.message;
  }

  // Handle user rejection
  if ([4001, 4100].includes(err.code)) {
    return error[Math.abs(err.code)];
  }

  return error.UNKNOWN_ERROR;
};


const IndexApprovedCampaign = () => {
  const { account, artistContract } = useWallet();
  const { setSpinner } = useGlobal();
  const { showError} = useError();

  const [campaigns, setCampaigns] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Process campaign data with art pack details
  const processCampaignData = useCallback(async (campaignList, artPacks) => {
    return Promise.all(
      campaignList.map(async (campaign) => {
        const artPack = artPacks.find((ap) => ap.sc_id === campaign.art_pack_id);
        
        try {
          const campaignDetails = await approvedCampaignAPI.getCampaignDetails(campaign.campaign_id);
          
          return {
            approved_campaign_id: campaign.approved_campaign_id,
            campaign_id: campaign.campaign_id,
            art_pack_id: campaign.art_pack_id,
            art_pack_name: artPack?.art_pack_name,
            min_artist_percentage: artPack?.min_artist_percentage,
            approved: campaign.approved,
            campaign_name: campaignDetails?.campaignName || `Unknown #${campaign.campaign_id}`,
          };
        } catch (err) {
          console.error(`Error processing campaign ${campaign.campaign_id}:`, err);
          return {
            approved_campaign_id: campaign.approved_campaign_id,
            campaign_id: campaign.campaign_id,
            art_pack_id: campaign.art_pack_id,
            art_pack_name: artPack?.art_pack_name,
            min_artist_percentage: artPack?.min_artist_percentage,
            approved: campaign.approved,
            campaign_name: `Unknown #${campaign.campaign_id}`,
          };
        }
      })
    );
  }, []);

  // Load approved campaigns data
  const loadCampaigns = useCallback(async () => {
    if (!account) return;

    setSpinner(true);
    try {
      const [campaignList, artPacks] = await Promise.all([
        approvedCampaignAPI.getAll(account),
        approvedCampaignAPI.getArtPacks(account),
      ]);

      const processedData = await processCampaignData(campaignList, artPacks);
      setCampaigns(processedData);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    } finally {
      setSpinner(false);
    }
  }, [account, setSpinner, processCampaignData]);

  // Load data when account changes
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Approve campaign on blockchain
  const handleApproveCampaign = useCallback(async (campaign) => {
    if (campaign.approved) return;

    setSpinner(true);
    try {
      await artistContract.approveCampaign(campaign.campaign_id);
      await loadCampaigns();
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Error approving campaign:", err);
      const errorMessage = parseContractError(err);
      showError(errorMessage);
    } finally {
      setSpinner(false);
    }
  }, [artistContract, loadCampaigns, setSpinner]);

  // Close success modal
  const handleCloseModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);


  return (
    <>
      <Title title="Pending approval" />
      <Description text={DESCRIPTION_TEXT} />
      <Table data={campaigns} onApprove={handleApproveCampaign} />
      
      <Modal show={showSuccessModal}>
        <Popup>
          <div className="row justify-content-center modal-build-text">
            {SUCCESS_MESSAGE}
          </div>
          <div className="row d-flex justify-content-center mt-4">
            <button
              type="button"
              className="btn bg-orange form-btn me-3"
              onClick={handleCloseModal}
            >
              OK
            </button>
          </div>
        </Popup>
      </Modal>
    </>
  );
};

export default IndexApprovedCampaign;
